import { isInStrikeZone, ZONE_BOUNDS } from "./utils";

export const calculatePitchMetrics = (pitches, totalPitches) => {
  const metrics = {
    count: pitches.length,
    usage: ((pitches.length / totalPitches) * 100).toFixed(1),
    velocities: pitches
      .filter((p) => p.velocity && !isNaN(Number(p.velocity)))
      .map((p) => Number(p.velocity)),
    inZoneCount: 0,
    strikeCount: 0,
    missedZoneCount: 0,
    totalMissDistance: 0,
    pitchesWithIntendedZone: 0,
  };

  metrics.avgVelo = metrics.velocities.length
    ? (
        metrics.velocities.reduce((a, b) => a + b, 0) /
        metrics.velocities.length
      ).toFixed(1)
    : "-";
  metrics.maxVelo = metrics.velocities.length
    ? Math.max(...metrics.velocities).toFixed(1)
    : "-";

  pitches.forEach((pitch) => {
    const x = pitch.location?.x ?? pitch.x;
    const y = pitch.location?.y ?? pitch.y;

    if (x !== undefined && y !== undefined && isInStrikeZone(x, y)) {
      metrics.strikeCount++;
    }

    if (pitch.intendedZone && (x !== undefined && y !== undefined)) {
      metrics.pitchesWithIntendedZone++;
      const center = calculateZoneCenter(
        pitch.intendedZone,
        pitch.zoneType || "standard"
      );

      if (
        isPitchInZone(
          x,
          y,
          pitch.intendedZone,
          pitch.zoneType || "standard"
        )
      ) {
        metrics.inZoneCount++;
      } else {
        metrics.missedZoneCount++;
        metrics.totalMissDistance += Math.sqrt(
          Math.pow(x - center.x, 2) +
            Math.pow(y - center.y, 2)
        );
      }
    }
  });

  metrics.accuracy = metrics.pitchesWithIntendedZone
    ? ((metrics.inZoneCount / metrics.pitchesWithIntendedZone) * 100).toFixed(1)
    : "-";
  metrics.strikeRate = ((metrics.strikeCount / metrics.count) * 100).toFixed(1);
  metrics.avgMissDistance = metrics.missedZoneCount
    ? (metrics.totalMissDistance / metrics.missedZoneCount).toFixed(2)
    : "-";

  return metrics;
};

const calculateZoneCenter = (zone, zoneType = "standard") => {
  const bounds = ZONE_BOUNDS[zoneType]?.[zone];
  if (!bounds) return { x: 0, y: 0 };
  const { xMin, xMax, yMin, yMax } = bounds;
  return {
    x: (xMin + xMax) / 2,
    y: (yMin + yMax) / 2,
  };
};

const isPitchInZone = (x, y, zone, zoneType = "standard") => {
  const bounds = ZONE_BOUNDS[zoneType]?.[zone];
  if (!bounds) return false;
  const { xMin, xMax, yMin, yMax } = bounds;
  return x >= xMin && x <= xMax && y >= yMin && y <= yMax;
};

export const processPitchDataForAdvance = (pitches) => {
  return pitches.reduce((acc, pitch) => {
    const type = pitch.type?.toUpperCase() || "UNKNOWN";

    if (!acc[type]) {
      acc[type] = {
        count: 0,
        totalVelo: 0,
        velocities: [],
        pitches: [],
        results: {
          ball: 0,
          called_strike: 0,
          strikeout_looking: 0,
          strikeout_swinging: 0,
          swinging_strike: 0,
          foul: 0,
          in_play: 0,
        },
        hardHit: 0,
        battedBalls: 0,
      };
    }

    acc[type].count++;
    acc[type].pitches.push(pitch);

    if (pitch.velocity) {
      const velocity = Number(pitch.velocity);
      if (!isNaN(velocity)) {
        acc[type].velocities.push(velocity);
        acc[type].totalVelo += velocity;
      }
    }

    if (pitch.result) {
      acc[type].results[pitch.result] =
        (acc[type].results[pitch.result] || 0) + 1;
    }

    if (pitch.hitDetails?.exitVelocity === "hard") {
      acc[type].hardHit++;
    }

    if (pitch.result === "in_play") {
      acc[type].battedBalls++;
    }

    const data = acc[type];
    data.usage = ((data.count / pitches.length) * 100).toFixed(1);
    data.avgVelo = data.velocities.length
      ? (data.totalVelo / data.velocities.length).toFixed(1)
      : "-";
    data.maxVelo = data.velocities.length
      ? Math.max(...data.velocities).toFixed(1)
      : "-";

    const calledStrikes =
      (data.results.called_strike || 0) + (data.results.strikeout_looking || 0);
    const whiffs =
      (data.results.swinging_strike || 0) +
      (data.results.strikeout_swinging || 0);
    data.cswRate =
      data.count > 0
        ? (((calledStrikes + whiffs) / data.count) * 100).toFixed(1)
        : "0.0";

    const strikes =
      (data.results.called_strike || 0) +
      (data.results.strikeout_looking || 0) +
      (data.results.foul || 0) +
      (data.results.swinging_strike || 0) +
      (data.results.strikeout_swinging || 0);
    data.strikeRate =
      data.count > 0 ? ((strikes / data.count) * 100).toFixed(1) : "0.0";

    data.hardHitRate = data.battedBalls
      ? ((data.hardHit / data.battedBalls) * 100).toFixed(1)
      : "0.0";

    const zoneBounds = ZONE_BOUNDS.standard;
    const isPitchInZone = (x, y, zone) => {
      if (x === undefined || y === undefined || x === null || y === null) {
        return false;
      }
      const bounds = zoneBounds[zone];
      if (!bounds) return false;
      return (
        x >= bounds.xMin &&
        x <= bounds.xMax &&
        y >= bounds.yMin &&
        y <= bounds.yMax
      );
    };

    const inZonePitches = data.pitches.filter((p) => {
      const px = p.location?.x ?? p.x;
      const py = p.location?.y ?? p.y;
      return [1, 2, 3, 4, 5, 6, 7, 8, 9].some((zone) =>
        isPitchInZone(px, py, zone)
      );
    }).length;
    data.zoneRate =
      data.count > 0 ? ((inZonePitches / data.count) * 100).toFixed(1) : "0.0";

    return acc;
  }, {});
};
